import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Newspaper, FileText, PenLine, BookOpen, ScrollText, Table2, Sparkles, Wand2, ListChecks,
  Loader2, Download, Mic, Square, Bold, Italic, Heading2, List, Paperclip, X, Library,
  Trash2, FolderOpen, HelpCircle, Check, Save, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Palette, Rows3, ClipboardCheck, Quote, Plus, GraduationCap,
} from "lucide-react";

const LEVELS = [
  { v: "1er_cycle", l: "1er cycle" }, { v: "2nd_cycle", l: "2nd cycle" },
  { v: "superieur", l: "Supérieur" }, { v: "administratif", l: "Administratif" },
];
const SUMMARY_LENS = [
  { v: "1/4", l: "1/4 du texte" }, { v: "1/3", l: "1/3 du texte" },
  { v: "500", l: "500 mots" }, { v: "300", l: "300 mots" }, { v: "250", l: "250 mots" },
  { v: "custom", l: "Personnalisé" },
];
const CITATION_STYLES = [
  { v: "vancouver", l: "Vancouver" }, { v: "apa", l: "APA" },
  { v: "chicago", l: "Chicago" }, { v: "mla", l: "MLA" },
];

const TYPES = [
  { key: "reportage", label: "Reportage d'événement", icon: Newspaper, desc: "Chapeau, corps, citations, conclusion" },
  { key: "article", label: "Article de journal", icon: FileText, desc: "Titre accrocheur, intro, développement, chute" },
  { key: "resume", label: "Résumé de texte", icon: PenLine, desc: "Résumé structuré avec identification des idées principales" },
  { key: "dissertation", label: "Dissertation littéraire", icon: BookOpen, desc: "Thèse, antithèse, synthèse" },
  { key: "commentaire", label: "Commentaire composé", icon: ScrollText, desc: "Axes : thème, style, figures, intention" },
  { key: "analyse_grammaticale", label: "Analyse logique & grammaticale", icon: Table2, desc: "Tableau nature / fonction + synthèse" },
];

const TEMPLATES = {
  reportage: "<h2>Chapeau</h2><p>Résumez l'essentiel de l'événement (qui, quoi, où, quand, pourquoi).</p><h2>Corps de l'article</h2><p>Relatez les faits en détail, dans l'ordre.</p><h2>Citation</h2><p>« … » — Témoin / acteur de l'événement.</p><h2>Conclusion</h2><p>Perspective, suite ou portée de l'événement.</p>",
  article: "<h2>Titre accrocheur</h2><p>…</p><h2>Introduction</h2><p>Posez le sujet et l'angle.</p><h2>Développement</h2><p>Informations, contexte, analyse.</p><h2>Chute</h2><p>Phrase finale marquante.</p>",
  resume: "<h2>Introduction du texte source</h2><p>Présentez brièvement le texte résumé.</p><h2>Idées principales</h2><p>Reformulez fidèlement les idées essentielles, dans l'ordre.</p><h2>Conclusion synthétique</h2><p>Synthèse finale.</p>",
  dissertation: "<h2>Introduction</h2><p>Amorce, problématique, annonce du plan.</p><h2>I. Thèse</h2><p>Premier axe argumenté avec exemples littéraires.</p><h2>II. Antithèse</h2><p>Nuance ou opposition, exemples.</p><h2>III. Synthèse</h2><p>Dépassement, position personnelle.</p><h2>Conclusion</h2><p>Bilan et ouverture.</p>",
  commentaire: "<h2>Introduction</h2><p>Présentation du texte, de l'auteur et de la problématique.</p><h2>Axe 1 — Thème</h2><p>…</p><h2>Axe 2 — Style &amp; figures de style</h2><p>…</p><h2>Axe 3 — Intention de l'auteur</h2><p>…</p><h2>Conclusion</h2><p>Synthèse des axes et ouverture.</p>",
  analyse_grammaticale: "",
};

const HELP = {
  reportage: ["Le reportage relate un événement vécu. Le chapeau résume l'essentiel ; le corps détaille les faits ; une citation donne du vivant ; la conclusion ouvre une perspective."],
  article: ["L'article de journal informe clairement. Un titre accrocheur, une introduction qui pose le sujet, un développement structuré et une chute marquante."],
  resume: ["Le résumé restitue fidèlement un texte source en identifiant ses idées principales, sans ajout d'avis. Choisissez la longueur et la source (éditeur ou pièce jointe), puis « Générer »."],
  dissertation: ["La dissertation littéraire argumente autour d'une problématique en trois temps : thèse, antithèse, synthèse, appuyés d'exemples littéraires précis."],
  commentaire: ["Le commentaire composé analyse un texte par axes (thème, style, figures de style, intention de l'auteur) sans paraphraser."],
  analyse_grammaticale: ["Saisissez une phrase (ou un court passage). L'IA produit un tableau mot/groupe → nature (classe) → fonction, avec une courte synthèse."],
};

const htmlToText = (html) => {
  const el = document.createElement("div");
  el.innerHTML = html || "";
  const walk = (node) => {
    let out = "";
    node.childNodes.forEach((c) => {
      if (c.nodeType === 3) { out += c.textContent; return; }
      const tag = c.tagName?.toLowerCase();
      if (tag === "h1") out += "\n# " + c.textContent.trim() + "\n";
      else if (tag === "h2") out += "\n## " + c.textContent.trim() + "\n";
      else if (tag === "h3") out += "\n### " + c.textContent.trim() + "\n";
      else if (tag === "li") out += "- " + c.textContent.trim() + "\n";
      else if (tag === "br") out += "\n";
      else if (tag === "p" || tag === "div") out += walk(c) + "\n";
      else out += walk(c);
    });
    return out;
  };
  return walk(el).replace(/\n{3,}/g, "\n\n").trim();
};

const textToHtml = (txt) => (txt || "").split(/\n{2,}/).map((p) => {
  const line = p.trim();
  if (line.startsWith("## ")) return `<h2>${line.slice(3)}</h2>`;
  if (line.startsWith("# ")) return `<h2>${line.slice(2)}</h2>`;
  return `<p>${line.replace(/\n/g, "<br>")}</p>`;
}).join("");

export default function LitteratureAtelier() {
  const [type, setType] = useState("reportage");
  const [title, setTitle] = useState("");
  const [level, setLevel] = useState("superieur");
  const [summaryLen, setSummaryLen] = useState("300");
  const [summaryCustom, setSummaryCustom] = useState("");
  const [summarySource, setSummarySource] = useState("editor");
  const [evaluation, setEvaluation] = useState(null);
  const [references, setReferences] = useState([]);
  const [citationStyle, setCitationStyle] = useState("vancouver");
  const [newRef, setNewRef] = useState("");
  const [status, setStatus] = useState("brouillon");
  const [html, setHtml] = useState("");
  const [sentence, setSentence] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [workId, setWorkId] = useState(null);
  const [busy, setBusy] = useState("");         // correct | improve | plan | grammar
  const [changes, setChanges] = useState(null); // {kind, items}
  const [plan, setPlan] = useState(null);
  const [grammar, setGrammar] = useState(null);
  const [exporting, setExporting] = useState("");
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [showLib, setShowLib] = useState(false);
  const [libStatus, setLibStatus] = useState("all");
  const [library, setLibrary] = useState([]);
  const [lineHeight, setLineHeight] = useState("1.6");

  const edRef = useRef(null);
  const selRef = useRef(null);
  const pollRef = useRef(null);
  const recRef = useRef(null);
  const recActive = useRef(false);
  const streamRef = useRef(null);

  useEffect(() => () => { pollRef.current && clearInterval(pollRef.current); stopRecording(); }, []);

  const setEditor = (h) => { setHtml(h); if (edRef.current) edRef.current.innerHTML = h || ""; };

  const chooseType = (k) => {
    setType(k); setWorkId(null); setTitle(""); setStatus("brouillon");
    setChanges(null); setPlan(null); setGrammar(null); setSentence(""); setEvaluation(null); setReferences([]);
    setAttachments([]);
    setEditor(TEMPLATES[k] || "");
  };

  useEffect(() => { setEditor(TEMPLATES[type] || ""); /* initial */ // eslint-disable-next-line
  }, []);

  const exec = (cmd, val) => { edRef.current?.focus(); document.execCommand(cmd, false, val || null); setHtml(edRef.current?.innerHTML || ""); };

  // Mémorise la position du curseur dans l'éditeur (pour insérer une citation au bon endroit)
  const saveSel = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && edRef.current && edRef.current.contains(sel.anchorNode)) {
      selRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  // -------- Attachments
  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/litterature/attach", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((p) => [...p, data]);
      }
      toast.success("Pièce(s) jointe(s) ajoutée(s)");
    } catch (err) { toast.error(apiError(err, "Fichier non exploitable")); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeAttach = (id) => setAttachments((p) => p.filter((a) => a.id !== id));
  const downloadAttach = async (a) => {
    try {
      const res = await api.get(`/litterature/attachment/${a.id}/download`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data); const el = document.createElement("a");
      el.href = u; el.download = a.filename; el.click(); URL.revokeObjectURL(u);
    } catch (err) { toast.error(apiError(err, "Téléchargement impossible")); }
  };

  // -------- Dictée vocale (segments ~5s)
  const sendSegment = async (blob) => {
    if (blob.size < 1200) return;
    try {
      const fd = new FormData(); fd.append("file", new File([blob], "seg.webm", { type: "audio/webm" }));
      const { data } = await api.post("/litterature/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const t = (data.text || "").trim();
      if (t && edRef.current) {
        edRef.current.focus();
        document.execCommand("insertText", false, (edRef.current.innerText.endsWith(" ") ? "" : " ") + t + " ");
        setHtml(edRef.current.innerHTML);
      }
    } catch { /* segment ignoré */ }
  };
  const recordLoop = (stream) => {
    if (!recActive.current) return;
    const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
    recRef.current = rec; const chunks = [];
    rec.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
    rec.onstop = async () => { await sendSegment(new Blob(chunks, { type: "audio/webm" })); if (recActive.current) recordLoop(stream); };
    rec.start();
    setTimeout(() => { if (rec.state !== "inactive") rec.stop(); }, 5000);
  };
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; recActive.current = true; setRecording(true); recordLoop(stream);
    } catch { toast.error("Microphone indisponible (autorisez l'accès)."); }
  };
  const stopRecording = () => {
    recActive.current = false; setRecording(false);
    try { recRef.current && recRef.current.state !== "inactive" && recRef.current.stop(); } catch { /* */ }
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* */ }
    streamRef.current = null;
  };

  // -------- IA
  const attachmentsText = () => attachments.map((a) => a.filename).join(", ");
  const runAi = (action) => {
    const content = action === "grammar" ? sentence : htmlToText(html);
    if (["correct", "improve", "evaluate"].includes(action) && content.trim().length < 5) { toast.error("Rédigez d'abord un peu de texte."); return; }
    if (action === "grammar" && sentence.trim().length < 2) { toast.error("Saisissez une phrase à analyser."); return; }
    let replaceGen = true;
    if (action === "generate" && htmlToText(html).trim().length > 10) {
      replaceGen = window.confirm("Remplacer le contenu de l'éditeur ?\n\nOK = remplacer · Annuler = ajouter à la suite");
    }
    const lenVal = summaryLen === "custom" ? summaryCustom : summaryLen;
    const src = summarySource === "attachment" ? (attachments.map((a) => a.text || "").join("\n\n") || attachmentsText()) : htmlToText(html);
    setBusy(action);
    if (pollRef.current) clearInterval(pollRef.current);
    if (action === "correct" || action === "improve") setChanges(null);
    if (action === "plan") setPlan(null);
    if (action === "grammar") setGrammar(null);
    if (action === "evaluate") setEvaluation(null);
    api.post("/litterature/ai", { action, work_type: type, content, attachments_text: attachmentsText(), level, title, summary_len: lenVal, summary_source: src, citation_style: citationStyle })
      .then(({ data }) => {
        pollRef.current = setInterval(async () => {
          try {
            const { data: j } = await api.get(`/litterature/job/${data.job_id}`);
            if (j.status === "done") {
              clearInterval(pollRef.current); setBusy("");
              const r = j.result || {};
              if (action === "correct") { setEditor(textToHtml(r.corrected || content)); setChanges({ kind: "correct", items: r.changes || [] }); toast.success("Texte corrigé"); }
              else if (action === "improve") { setEditor(textToHtml(r.improved || content)); setChanges({ kind: "improve", items: r.suggestions || [] }); toast.success("Texte amélioré"); }
              else if (action === "plan") { setPlan(r); toast.success("Plan proposé"); }
              else if (action === "grammar") { setGrammar(r); toast.success("Analyse réalisée"); }
              else if (action === "generate") { const gen = textToHtml(r.generated || ""); setEditor(replaceGen ? gen : (edRef.current?.innerHTML || "") + gen); toast.success("Texte généré"); }
              else if (action === "evaluate") { setEvaluation(r); toast.success(`Note : ${r.note_globale ?? "?"}/20`); }
              else if (action === "refs") { setReferences((p) => [...p, ...(r.references || [])]); toast.success("Références suggérées"); }
            } else if (j.status === "error") { clearInterval(pollRef.current); setBusy(""); toast.error(j.error || "Service IA indisponible"); }
          } catch { clearInterval(pollRef.current); setBusy(""); }
        }, 2500);
      })
      .catch((err) => { setBusy(""); toast.error(apiError(err, "Service IA indisponible")); });
  };

  const insertCitation = (ref) => {
    const marker = citationStyle === "apa" ? ` (${(ref.auteurs || "Auteur").split(" ")[0]}, ${ref.annee || "s.d."})` : ` [${references.indexOf(ref) + 1}]`;
    const ed = edRef.current;
    if (!ed) return;
    ed.focus();
    const sel = window.getSelection();
    let range = selRef.current;
    if (range && ed.contains(range.startContainer)) {
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      range = document.createRange();
      range.selectNodeContents(ed);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    range = sel.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(marker);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    selRef.current = range.cloneRange();
    setHtml(ed.innerHTML);
  };
  const addManualRef = () => { if (newRef.trim()) { setReferences((p) => [...p, { formatee: newRef.trim() }]); setNewRef(""); } };

  const insertPlan = () => { if (plan?.plan_markdown) { setEditor((edRef.current?.innerHTML || "") + textToHtml(plan.plan_markdown)); toast.success("Plan inséré dans l'éditeur"); } };

  // -------- Archivage
  const save = async (st) => {
    const newStatus = st || status;
    const content = type === "analyse_grammaticale" ? `<p>${sentence}</p>` : (edRef.current?.innerHTML || html);
    if (htmlToText(content).trim().length < 1 && !attachments.length) { toast.error("Rien à enregistrer."); return; }
    setSaving(true);
    try {
      const meta = { level, citationStyle, references, evaluation,
        ...(type === "analyse_grammaticale" && grammar ? { grammar } : {}) };
      const { data } = await api.post("/litterature/works", {
        id: workId, type, title, content, status: newStatus,
        attachments: attachments.map((a) => ({ id: a.id, filename: a.filename, size: a.size, content_type: a.content_type })),
        meta,
      });
      setWorkId(data.id); setStatus(newStatus); if (data.title && !title) setTitle(data.title);
      toast.success(newStatus === "finalise" ? "Travail finalisé et archivé" : "Brouillon enregistré");
      if (showLib) loadLibrary();
    } catch (err) { toast.error(apiError(err, "Enregistrement impossible")); } finally { setSaving(false); }
  };

  const loadLibrary = useCallback(async () => {
    try {
      const { data } = await api.get("/litterature/works", { params: libStatus !== "all" ? { status: libStatus } : {} });
      setLibrary(data.works || []);
    } catch { /* */ }
  }, [libStatus]);
  useEffect(() => { if (showLib) loadLibrary(); }, [showLib, libStatus, loadLibrary]);

  const openWork = async (id) => {
    try {
      const { data } = await api.get(`/litterature/works/${id}`);
      setType(data.type); setTitle(data.title || ""); setStatus(data.status || "brouillon");
      setWorkId(data.id); setAttachments(data.attachments || []);
      setChanges(null); setPlan(null);
      const mt = data.meta || {};
      setLevel(mt.level || "superieur"); setCitationStyle(mt.citationStyle || "vancouver");
      setReferences(mt.references || []); setEvaluation(mt.evaluation || null);
      if (data.type === "analyse_grammaticale") { setSentence(htmlToText(data.content || "")); setGrammar(data.meta?.grammar || null); setEditor(""); }
      else { setGrammar(null); setSentence(""); setEditor(data.content || ""); }
      setShowLib(false); toast.success("Travail rouvert");
    } catch (err) { toast.error(apiError(err, "Ouverture impossible")); }
  };
  const deleteWork = async (id, e) => {
    e?.stopPropagation();
    try { await api.delete(`/litterature/works/${id}`); if (id === workId) setWorkId(null); loadLibrary(); toast.success("Travail supprimé"); }
    catch (err) { toast.error(apiError(err, "Suppression impossible")); }
  };

  // -------- Export (réutilise /recherche/ai/export, logo CARIS)
  const exportDoc = async (format) => {
    const content = type === "analyse_grammaticale"
      ? `# ${title || "Analyse grammaticale"}\n\n## Phrase analysée\n${sentence}\n\n## Analyse\n` +
        (grammar?.rows || []).map((r) => `- ${r.segment} — ${r.nature} — ${r.fonction}`).join("\n") +
        (grammar?.synthese ? `\n\n## Synthèse\n${grammar.synthese}` : "")
      : (title ? `# ${title}\n\n` : "") + htmlToText(html);
    const lvlLabel = LEVELS.find((l) => l.v === level)?.l || "";
    let extra = `\n\n## Niveau de l'apprenant\n${lvlLabel}`;
    if (evaluation) {
      extra += `\n\n## Barème d'évaluation — Note : ${evaluation.note_globale ?? "?"} / ${evaluation.sur || 20}\n`;
      extra += (evaluation.criteres || []).map((c) => `- ${c.critere} : ${c.note}/${c.max} — ${c.commentaire || ""}`).join("\n");
      if (evaluation.appreciation_generale) extra += `\n\nAppréciation générale : ${evaluation.appreciation_generale}`;
    }
    if (references.length) {
      extra += `\n\n## Références bibliographiques (${CITATION_STYLES.find((s) => s.v === citationStyle)?.l || ""})\n`;
      extra += references.map((r, i) => `- [${i + 1}] ${r.formatee || `${r.auteurs || ""} (${r.annee || ""}). ${r.titre || ""}. ${r.source || ""}`}`).join("\n");
    }
    const fullContent = content + extra;
    if (fullContent.trim().length < 3) { toast.error("Rien à exporter."); return; }
    setExporting(format);
    try {
      const res = await api.post("/recherche/ai/export", { title: title || TYPES.find((t) => t.key === type)?.label || "Travail", content: fullContent, format, draft: status !== "finalise" }, { responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = u; a.download = `redaction-litteraire.${format === "docx" ? "docx" : "pdf"}`; a.click(); URL.revokeObjectURL(u);
    } catch (err) { toast.error(apiError(err, "Export indisponible")); } finally { setExporting(""); }
  };

  const isGrammar = type === "analyse_grammaticale";
  const curType = TYPES.find((t) => t.key === type);

  return (
    <div className="mt-6" data-testid="litt-root">
      <div className="mb-5 flex items-center justify-end">
        <Button onClick={() => setShowLib((v) => !v)} data-testid="litt-library-toggle" variant="outline" className="rounded-full border-[#0E7C7B] text-[#0E7C7B]">
          <Library size={16} className="mr-1.5" /> Mes travaux{library.length ? ` (${library.length})` : ""}
        </Button>
      </div>

      {showLib && (
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid="litt-library">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-navy">Mes travaux archivés</p>
            <button onClick={() => setShowLib(false)} data-testid="litt-library-close" className="text-slate-400 hover:text-navy"><X size={18} /></button>
          </div>
          <div className="mt-3 flex gap-2">
            {[["all", "Tous"], ["brouillon", "Brouillons"], ["finalise", "Finalisés"]].map(([v, l]) => (
              <button key={v} onClick={() => setLibStatus(v)} data-testid={`litt-lib-filter-${v}`} className={`rounded-full px-3 py-1 text-xs font-medium ${libStatus === v ? "bg-navy text-white" : "bg-slate-100 text-slate-500"}`}>{l}</button>
            ))}
          </div>
          {library.length === 0 ? <p className="mt-3 text-sm text-slate-400">Aucun travail pour l'instant.</p> : (
            <ul className="mt-3 space-y-2">
              {library.map((w) => (
                <li key={w.id} onClick={() => openWork(w.id)} data-testid={`litt-work-${w.id}`} className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2 hover:border-[#0E7C7B]">
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="truncate font-medium text-navy">{w.title || "Sans titre"}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${w.status === "finalise" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{w.status === "finalise" ? "Finalisé" : "Brouillon"}</span>
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-400">{w.type_label}{w.attachments?.length ? ` · ${w.attachments.length} pièce(s) jointe(s)` : ""}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <FolderOpen size={15} className="text-slate-300" />
                    <button onClick={(e) => deleteWork(w.id, e)} data-testid={`litt-work-delete-${w.id}`} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <div className="space-y-3" data-testid="litt-types">
          {TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => chooseType(t.key)} data-testid={`litt-type-${t.key}`}
                className={`w-full rounded-2xl border p-4 text-left transition ${type === t.key ? "border-[#0E7C7B] bg-[#0E7C7B]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <p className="flex items-center gap-2 font-semibold text-navy"><Icon size={16} className="text-[#0E7C7B]" /> {t.label}</p>
                <p className="mt-0.5 text-xs text-slate-500">{t.desc}</p>
              </button>
            );
          })}
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 font-semibold text-navy">{curType?.label}
                <button onClick={() => setHelpOpen(true)} data-testid="litt-help-btn" className="text-slate-300 hover:text-[#0E7C7B]"><HelpCircle size={16} /></button>
              </h3>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${status === "finalise" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="litt-status-badge">{status === "finalise" ? "Finalisé" : "Brouillon"}</span>
            </div>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre du travail" data-testid="litt-title" className="mt-3" />

            {!isGrammar && (
              <div className="mt-3 flex flex-wrap items-end gap-3" data-testid="litt-config">
                <div>
                  <label className="flex items-center gap-1 text-xs text-slate-500"><GraduationCap size={13} className="text-[#0E7C7B]" /> Niveau de l'apprenant</label>
                  <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="litt-level" className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-navy">
                    {LEVELS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
                  </select>
                </div>
                {type === "resume" && (
                  <>
                    <div>
                      <label className="text-xs text-slate-500">Longueur du résumé</label>
                      <select value={summaryLen} onChange={(e) => setSummaryLen(e.target.value)} data-testid="litt-summary-len" className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-navy">
                        {SUMMARY_LENS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                      </select>
                    </div>
                    {summaryLen === "custom" && (
                      <Input value={summaryCustom} onChange={(e) => setSummaryCustom(e.target.value)} placeholder="ex. 400 mots" data-testid="litt-summary-custom" className="w-36" />
                    )}
                    <div>
                      <label className="text-xs text-slate-500">Texte source</label>
                      <select value={summarySource} onChange={(e) => setSummarySource(e.target.value)} data-testid="litt-summary-source" className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-navy">
                        <option value="editor">Contenu de l'éditeur</option>
                        <option value="attachment">Pièces jointes</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            )}

            {isGrammar ? (
              <div className="mt-4">
                <label className="text-xs text-slate-500">Phrase (ou court passage) à analyser</label>
                <textarea value={sentence} onChange={(e) => setSentence(e.target.value)} rows={3} data-testid="litt-sentence" placeholder="Ex. : Le petit chat dort paisiblement sur le canapé." className="mt-1 w-full rounded-lg border border-slate-200 p-3 text-sm text-navy" />
                <Button onClick={() => runAi("grammar")} disabled={busy === "grammar"} data-testid="litt-grammar-run" className="mt-3 rounded-full bg-navy text-sm text-white hover:bg-navy-light">
                  {busy === "grammar" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Table2 size={15} className="mr-1" />} Analyser la phrase
                </Button>
                {grammar && (
                  <div className="mt-4" data-testid="litt-grammar-result">
                    <table className="w-full text-left text-xs">
                      <thead><tr className="border-b border-slate-200 text-slate-500">{["Mot / groupe", "Nature", "Fonction"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
                      <tbody>{(grammar.rows || []).map((r, k) => (
                        <tr key={k} className="border-b border-slate-100 align-top"><td className="px-2 py-1.5 font-medium text-navy">{r.segment}</td><td className="px-2 py-1.5 text-[#0E7C7B]">{r.nature}</td><td className="px-2 py-1.5 text-slate-600">{r.fonction}</td></tr>
                      ))}</tbody>
                    </table>
                    {grammar.synthese && <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600"><b className="text-navy">Synthèse. </b>{grammar.synthese}</p>}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="mt-4 flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="litt-toolbar">
                  <button onClick={() => exec("bold")} data-testid="litt-fmt-bold" className="rounded p-1.5 text-slate-600 hover:bg-white" title="Gras"><Bold size={15} /></button>
                  <button onClick={() => exec("italic")} data-testid="litt-fmt-italic" className="rounded p-1.5 text-slate-600 hover:bg-white" title="Italique"><Italic size={15} /></button>
                  <button onClick={() => exec("formatBlock", "<h2>")} data-testid="litt-fmt-h2" className="rounded p-1.5 text-slate-600 hover:bg-white" title="Titre"><Heading2 size={15} /></button>
                  <button onClick={() => exec("insertUnorderedList")} data-testid="litt-fmt-list" className="rounded p-1.5 text-slate-600 hover:bg-white" title="Liste"><List size={15} /></button>
                  <span className="mx-1 h-5 w-px bg-slate-200" />
                  <button onClick={() => exec("justifyLeft")} data-testid="litt-fmt-left" className="rounded p-1.5 text-slate-600 hover:bg-white" title="Aligner à gauche"><AlignLeft size={15} /></button>
                  <button onClick={() => exec("justifyCenter")} data-testid="litt-fmt-center" className="rounded p-1.5 text-slate-600 hover:bg-white" title="Centrer"><AlignCenter size={15} /></button>
                  <button onClick={() => exec("justifyRight")} data-testid="litt-fmt-right" className="rounded p-1.5 text-slate-600 hover:bg-white" title="Aligner à droite"><AlignRight size={15} /></button>
                  <button onClick={() => exec("justifyFull")} data-testid="litt-fmt-justify" className="rounded p-1.5 text-slate-600 hover:bg-white" title="Justifier"><AlignJustify size={15} /></button>
                  <span className="mx-1 h-5 w-px bg-slate-200" />
                  <label data-testid="litt-fmt-color" className="flex cursor-pointer items-center rounded p-1.5 text-slate-600 hover:bg-white" title="Couleur du texte">
                    <Palette size={15} />
                    <input type="color" onChange={(e) => exec("foreColor", e.target.value)} className="ml-1 h-5 w-5 cursor-pointer border-0 bg-transparent p-0" data-testid="litt-fmt-color-input" />
                  </label>
                  <span className="flex items-center gap-1 pl-1" title="Interligne">
                    <Rows3 size={15} className="text-slate-600" />
                    <select value={lineHeight} onChange={(e) => setLineHeight(e.target.value)} data-testid="litt-fmt-spacing" className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs text-slate-600">
                      <option value="1.4">Serré</option>
                      <option value="1.6">Normal</option>
                      <option value="2.0">Large</option>
                      <option value="2.5">Double</option>
                    </select>
                  </span>
                  <span className="mx-1 h-5 w-px bg-slate-200" />
                  <button onClick={() => recording ? stopRecording() : startRecording()} data-testid="litt-mic" className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${recording ? "bg-red-100 text-red-600" : "text-slate-600 hover:bg-white"}`} title="Dictée vocale">
                    {recording ? <><Square size={13} /> Arrêter</> : <><Mic size={13} /> Dicter</>}
                  </button>
                  {recording && <span className="text-[11px] text-red-500">● Enregistrement… la voix s'ajoute par segments</span>}
                </div>
                <div ref={edRef} contentEditable suppressContentEditableWarning data-testid="litt-editor"
                  onInput={(e) => { setHtml(e.currentTarget.innerHTML); saveSel(); }}
                  onKeyUp={saveSel} onMouseUp={saveSel} onBlur={saveSel} style={{ lineHeight }}
                  className="litt-editor mt-2 min-h-[260px] rounded-xl border border-slate-200 p-4 text-sm text-slate-700 focus:border-[#0E7C7B] focus:outline-none" />
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button onClick={() => runAi("correct")} disabled={!!busy} data-testid="litt-ai-correct" className="rounded-full bg-navy text-sm text-white hover:bg-navy-light">
                    {busy === "correct" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Check size={15} className="mr-1" />} Corriger avec l'IA
                  </Button>
                  <Button onClick={() => runAi("improve")} disabled={!!busy} data-testid="litt-ai-improve" variant="outline" className="rounded-full border-[#0E7C7B] text-sm text-[#0E7C7B]">
                    {busy === "improve" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Wand2 size={15} className="mr-1" />} Améliorer avec l'IA
                  </Button>
                  <Button onClick={() => runAi("plan")} disabled={!!busy} data-testid="litt-ai-plan" variant="outline" className="rounded-full border-slate-300 text-sm text-slate-600">
                    {busy === "plan" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <ListChecks size={15} className="mr-1" />} Suggérer un plan
                  </Button>
                  <Button onClick={() => runAi("generate")} disabled={!!busy} data-testid="litt-ai-generate" className="rounded-full bg-[#0E7C7B] text-sm text-white hover:bg-[#0E7C7B]/90">
                    {busy === "generate" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Sparkles size={15} className="mr-1" />} Générer avec l'IA
                  </Button>
                  <Button onClick={() => runAi("evaluate")} disabled={!!busy} data-testid="litt-ai-evaluate" variant="outline" className="rounded-full border-amber-400 text-sm text-amber-600 hover:bg-amber-50">
                    {busy === "evaluate" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <ClipboardCheck size={15} className="mr-1" />} Évaluer /20
                  </Button>
                </div>
              </>
            )}
          </div>

          {changes && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="litt-changes">
              <p className="font-semibold text-navy">{changes.kind === "correct" ? "Corrections appliquées" : "Propositions d'amélioration"}</p>
              {changes.items.length === 0 ? <p className="mt-2 text-sm text-emerald-600">Aucune modification majeure — le texte était déjà de bonne qualité.</p> : (
                <ul className="mt-3 space-y-2">
                  {changes.items.map((c, k) => (
                    <li key={k} className="rounded-xl bg-slate-50 p-3 text-xs" data-testid={`litt-change-${k}`}>
                      <span className="text-red-500 line-through">{c.before}</span> <span className="mx-1 text-slate-400">→</span> <span className="font-medium text-emerald-700">{c.after}</span>
                      {(c.explanation || c.note) && <p className="mt-1 text-slate-500">{c.explanation || c.note}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {plan && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="litt-plan">
              <div className="flex items-center justify-between"><p className="font-semibold text-navy">Plan proposé</p>
                <Button onClick={insertPlan} data-testid="litt-plan-insert" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">Insérer dans l'éditeur</Button>
              </div>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-slate-600">{plan.plan_markdown}</pre>
              {plan.conseils?.length > 0 && <ul className="mt-2 list-disc pl-5 text-sm text-slate-500">{plan.conseils.map((c, k) => <li key={k}>{c}</li>)}</ul>}
            </div>
          )}

          {evaluation && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5" data-testid="litt-evaluation">
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 font-semibold text-navy"><ClipboardCheck size={17} className="text-amber-600" /> Évaluation notée</p>
                <span className="rounded-full bg-amber-500 px-3 py-1 text-sm font-bold text-white" data-testid="litt-eval-note">{evaluation.note_globale ?? "?"} / {evaluation.sur || 20}</span>
              </div>
              {(evaluation.criteres || []).length > 0 && (
                <table className="mt-3 w-full text-left text-xs">
                  <thead><tr className="border-b border-amber-200 text-slate-500">{["Critère", "Note", "Appréciation"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
                  <tbody>{evaluation.criteres.map((c, k) => (
                    <tr key={k} className="border-b border-amber-100 align-top" data-testid={`litt-eval-crit-${k}`}>
                      <td className="px-2 py-1.5 font-medium text-navy">{c.critere}</td>
                      <td className="px-2 py-1.5 font-semibold text-amber-700">{c.note}/{c.max}</td>
                      <td className="px-2 py-1.5 text-slate-600">{c.commentaire}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
              {evaluation.appreciation_generale && <p className="mt-3 rounded-xl bg-white p-3 text-sm text-slate-600"><b className="text-navy">Appréciation générale. </b>{evaluation.appreciation_generale}</p>}
            </div>
          )}

          {!isGrammar && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="litt-references">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 font-semibold text-navy"><Quote size={16} className="text-[#0E7C7B]" /> Références bibliographiques</p>
              <div className="flex items-center gap-2">
                <select value={citationStyle} onChange={(e) => setCitationStyle(e.target.value)} data-testid="litt-ref-style" className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-navy">
                  {CITATION_STYLES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
                <Button onClick={() => runAi("refs")} disabled={!!busy} data-testid="litt-ref-suggest" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">
                  {busy === "refs" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Sparkles size={13} className="mr-1" />} Suggérer (IA)
                </Button>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Input value={newRef} onChange={(e) => setNewRef(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addManualRef()} placeholder="Ajouter une référence manuellement…" data-testid="litt-ref-input" className="flex-1" />
              <Button onClick={addManualRef} data-testid="litt-ref-add" variant="outline" className="rounded-full border-slate-300 text-slate-600"><Plus size={15} /></Button>
            </div>
            {references.length === 0 ? <p className="mt-3 text-sm text-slate-400">Aucune référence. Suggérez-en via l'IA ou ajoutez-les manuellement.</p> : (
              <ol className="mt-3 space-y-2 text-sm">
                {references.map((r, i) => (
                  <li key={i} data-testid={`litt-ref-${i}`} className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
                    <span className="min-w-0 text-slate-600"><span className="mr-1 font-semibold text-navy">[{i + 1}]</span>{r.formatee || `${r.auteurs || ""} (${r.annee || ""}). ${r.titre || ""}. ${r.source || ""}`}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      <button onMouseDown={(e) => e.preventDefault()} onClick={() => insertCitation(r)} data-testid={`litt-ref-insert-${i}`} title="Insérer l'appel de citation" className="text-slate-400 hover:text-[#0E7C7B]"><Quote size={15} /></button>
                      <button onClick={() => setReferences((p) => p.filter((_, k) => k !== i))} data-testid={`litt-ref-remove-${i}`} className="text-slate-400 hover:text-red-500"><X size={15} /></button>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="litt-attachments">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-navy">Pièces jointes</p>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:border-[#0E7C7B]">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} Ajouter (.pdf, .docx, .txt)
                <input type="file" multiple accept=".pdf,.docx,.txt" onChange={onAttach} className="hidden" data-testid="litt-attach-input" />
              </label>
            </div>
            {attachments.length === 0 ? <p className="mt-2 text-sm text-slate-400">Aucune pièce jointe. L'IA tient compte du contenu des fichiers ajoutés.</p> : (
              <ul className="mt-3 space-y-2">
                {attachments.map((a) => (
                  <li key={a.id} data-testid={`litt-attach-${a.id}`} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <button onClick={() => downloadAttach(a)} className="flex min-w-0 items-center gap-2 text-navy hover:underline"><FileText size={15} className="shrink-0 text-[#0E7C7B]" /> <span className="truncate">{a.filename}</span></button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => downloadAttach(a)} data-testid={`litt-attach-dl-${a.id}`} className="text-slate-400 hover:text-[#0E7C7B]"><Download size={15} /></button>
                      <button onClick={() => removeAttach(a.id)} data-testid={`litt-attach-rm-${a.id}`} className="text-slate-400 hover:text-red-500"><X size={16} /></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="litt-actions">
            <Button onClick={() => save("brouillon")} disabled={saving} data-testid="litt-save-draft" variant="outline" className="rounded-full border-navy text-sm text-navy">
              {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Enregistrer le brouillon
            </Button>
            <Button onClick={() => save("finalise")} disabled={saving} data-testid="litt-save-final" className="rounded-full bg-[#0E7C7B] text-sm text-white hover:bg-[#0E7C7B]/90">
              <Check size={14} className="mr-1" /> Finaliser
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <span className="text-xs font-medium text-slate-400">Exporter :</span>
            <Button onClick={() => exportDoc("docx")} disabled={!!exporting} data-testid="litt-export-docx" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exporting === "docx" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Word</Button>
            <Button onClick={() => exportDoc("pdf")} disabled={!!exporting} data-testid="litt-export-pdf" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exporting === "pdf" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} PDF</Button>
          </div>
        </div>
      </div>

      {helpOpen && (
        <>
          <div className="fixed inset-0 z-[70] bg-black/30" data-testid="litt-help-overlay" onClick={() => setHelpOpen(false)} />
          <aside className="fixed right-0 top-0 z-[71] flex h-full w-full max-w-sm flex-col bg-white shadow-2xl" data-testid="litt-help-panel">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <span className="flex items-center gap-2 font-semibold text-navy"><HelpCircle size={18} className="text-[#0E7C7B]" /> Aide — {curType?.label}</span>
              <button onClick={() => setHelpOpen(false)} data-testid="litt-help-close" className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3">
              {(HELP[type] || []).map((p, i) => <p key={i} className="text-sm leading-relaxed text-slate-600">{p}</p>)}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">Astuce : utilisez « Corriger » puis « Améliorer », et joignez des documents pour que l'IA en tienne compte.</div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
